/**
 * set_clause_soa — structural agent tool. Sets / edits / reverts the
 * Statement of Applicability decision for one clause in a Compass
 * folder. Wraps the SoA upsert endpoint with a preview/confirm cycle.
 *
 * Use `in_scope` to revert a prior SoA decision (the entry is
 * superseded, not deleted — audit trail preserved). Use the other
 * statuses with a real justification; auditors read the justification
 * verbatim.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';

function getFolderId(context: IAgentToolContext): string | null {
  const ctx = context.assistantContext as Record<string, unknown> | undefined;
  const f = ctx?.folderId;
  return typeof f === 'string' && f.length > 0 ? f : null;
}

type SoaStatus = 'in_scope' | 'excluded' | 'compensating' | 'deferred';
const STATUSES: readonly SoaStatus[] = ['in_scope', 'excluded', 'compensating', 'deferred'];

export const setClauseSoaTool: IAgentTool = {
  name: 'set_clause_soa',
  description:
    "Set the Statement of Applicability decision on one clause: in_scope (revert/default), excluded (clause doesn't apply), compensating (alternative control in place), or deferred (deferred to a later period). Returns a preview the user must confirm before persisting.",
  parameters: {
    type: 'object',
    properties: {
      frameworkRecordId: { type: 'string', description: 'UUID of the framework record.' },
      clauseRecordId: { type: 'string', description: 'UUID of the clause record.' },
      status: {
        type: 'string',
        description: 'in_scope | excluded | compensating | deferred.',
      },
      justification: {
        type: 'string',
        description:
          'Auditors read this verbatim — be specific. Required for excluded/compensating/deferred.',
      },
      nextReviewAt: {
        type: 'string',
        description: 'ISO timestamp for when this decision should be reviewed (optional).',
      },
    },
    required: ['frameworkRecordId', 'clauseRecordId', 'status', 'justification'],
  },
  supportedDiagramTypes: [],
  tier: 'structural',
  category: 'edit',

  async execute(params, context): Promise<AgentToolResult> {
    const folderId = getFolderId(context);
    if (!folderId) return { success: false, summary: 'No folder context available.' };
    const frameworkRecordId = String(params.frameworkRecordId ?? '');
    const clauseRecordId = String(params.clauseRecordId ?? '');
    const status = String(params.status ?? '') as SoaStatus;
    const justification = String(params.justification ?? '').trim();
    const nextReviewAt =
      typeof params.nextReviewAt === 'string' ? params.nextReviewAt : undefined;

    if (!frameworkRecordId || !clauseRecordId) {
      return { success: false, summary: 'frameworkRecordId and clauseRecordId are required.' };
    }
    if (!STATUSES.includes(status)) {
      return {
        success: false,
        summary: `Unknown status "${status}". Use one of: ${STATUSES.join(', ')}.`,
      };
    }
    if (!justification) {
      return { success: false, summary: 'justification is required.' };
    }

    const url =
      `/api/compass/folders/${encodeURIComponent(folderId)}` +
      `/audit/${encodeURIComponent(frameworkRecordId)}` +
      `/clauses/${encodeURIComponent(clauseRecordId)}/soa`;

    return {
      success: true,
      summary: `Ready to set SoA status to "${status}" with justification. Confirm to persist.`,
      uiSurface: 'chat',
      preview: {
        description: `Set SoA: ${status}\n\nJustification:\n${justification}`,
        delta: { additions: [], removals: [], moves: [], updates: [] },
        confirm: {
          kind: 'external-effect',
          run: async () => {
            const r = await fetch(url, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ status, justification, nextReviewAt: nextReviewAt ?? null }),
            });
            if (!r.ok) throw new Error(`SoA set failed (${r.status})`);
          },
        },
      },
    };
  },
};
